import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { ApiService } from '../api-service/api.service';
import { SocketService } from '../socket-service/socket.service';
import { map, distinctUntilChanged } from 'rxjs/operators';

export interface DropdownNotification {
  id_notification: string;
  titre_fr: string;
  description_fr: string;
  priorite: 'HIGH' | 'NORMAL' | 'LOW';
  code_type_objet: string;
  date_creation: string;
  flag_lu: 'O' | 'N';
}

export interface NotificationListItem {
  id_notification: string;
  titre_fr: string;
  description_fr: string;
  priorite: 'HIGH' | 'NORMAL' | 'LOW';
  code_type_objet: string;
  date_creation: string;
  date_lecture?: string;
  flag_lu: 'O' | 'N';
  type_notification?: string;
  created_by_name?: string;
}

export interface NotificationStats {
  total_notifications: number;
  unread_count: number;
  read_count: number;
  high_priority_unread: number;
  latest_notification_date?: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationDataService {
  private dropdownNotificationsSubject = new BehaviorSubject<DropdownNotification[]>([]);
  private unreadCountSubject = new BehaviorSubject<number>(0);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);

  public dropdownNotifications$ = this.dropdownNotificationsSubject.asObservable();
  public unreadCount$ = this.unreadCountSubject.asObservable();
  public isLoading$ = this.isLoadingSubject.asObservable();

  constructor(
    private apiService: ApiService,
    private socketService: SocketService
  ) {
    this.initializeSocketIntegration();
  }

  private initializeSocketIntegration(): void {
    // Only listen to socket unread count updates (real-time)
    this.socketService.unreadCount$.subscribe((socketUnreadCount) => {
      this.unreadCountSubject.next(socketUnreadCount);
    });

    // Handle cross-device notification updates
    this.socketService.onDropdownUpdate().subscribe((update) => {
      if (update && update.notifications) {
        this.dropdownNotificationsSubject.next(update.notifications);
      }
    });

    // Listen for new notifications and refresh dropdown
    this.socketService.notifications$.subscribe((socketNotifications) => {
      if (socketNotifications && socketNotifications.length > 0) {
        // Don't merge, just refresh the dropdown from API
        this.loadDropdownNotifications();
      }
    });
  }

  private mergeNotifications(apiNotifications: DropdownNotification[], socketNotifications: any[]): DropdownNotification[] {
    const merged = [...apiNotifications];
    
    socketNotifications.forEach(socketNotif => {
      const exists = merged.find(n => n.id_notification === socketNotif.id_notification);
      if (!exists) {
        const dropdownNotif: DropdownNotification = {
          id_notification: socketNotif.id_notification,
          titre_fr: socketNotif.titre_fr,
          description_fr: socketNotif.description_fr,
          priorite: socketNotif.priorite || 'NORMAL',
          code_type_objet: socketNotif.code_type_objet || '',
          date_creation: socketNotif.date_creation || new Date().toISOString(),
          flag_lu: 'N'
        };
        merged.unshift(dropdownNotif);
      }
    });

    return merged
      .sort((a, b) => new Date(b.date_creation).getTime() - new Date(a.date_creation).getTime())
      .slice(0, 10);
  }

  // Load dropdown notifications from API
  loadDropdownNotifications(): void {
    this.isLoadingSubject.next(true);
    
    this.apiService.get('notifications-ptr/dropdown').subscribe({
      next: (response) => {
        if (response.success) {
          this.dropdownNotificationsSubject.next(response.data || []);
          this.unreadCountSubject.next(response.unread_count || 0);
        }
        this.isLoadingSubject.next(false);
      },
      error: (error) => {
        console.error('Error loading dropdown notifications:', error);
        this.isLoadingSubject.next(false);
      }
    });
  }

  // Get paginated notifications list
  getNotificationsList(page: number = 1, limit: number = 20, filters: any = {}): Observable<any> {
    return this.apiService.get(`notifications-ptr/list/${page}/${limit}`, filters);
  }

  // Get unread count
  getUnreadCount(): Observable<any> {
    return this.apiService.get('notifications-ptr/unread-count');
  }

  // Mark single notification as read
  markAsRead(notificationId: string): Observable<any> {
    // Also mark via socket for real-time sync
    this.socketService.markNotificationAsRead(notificationId);
    
    return this.apiService.put(`notifications-ptr/${notificationId}/mark-read`);
  }

  // Mark multiple notifications as read
  markMultipleAsRead(notificationIds: string[]): Observable<any> {
    const body = { notification_ids: notificationIds };
    return this.apiService.put('notifications-ptr/mark-read-multiple', body);
  }

  // Mark all notifications as read
  markAllAsRead(): Observable<any> {
    return this.apiService.put('notifications-ptr/mark-all-read');
  }

  // Open notification (marks as read and returns details)
  openNotification(notificationId: string): Observable<any> {
    // Also mark via socket for real-time sync
    this.socketService.openNotification(notificationId);
    
    return this.apiService.get(`notifications-ptr/${notificationId}/open`);
  }

  // Delete notification
  deleteNotification(notificationId: string): Observable<any> {
    return this.apiService.delete(`notifications-ptr/${notificationId}`);
  }

  // Get current dropdown notifications
  getCurrentDropdownNotifications(): DropdownNotification[] {
    return this.dropdownNotificationsSubject.value;
  }

  // Get current unread count
  getCurrentUnreadCount(): number {
    return this.unreadCountSubject.value;
  }

  // Refresh all notification data
  refreshAll(): void {
    this.loadDropdownNotifications();
    this.socketService.requestUnreadCount();
  }
}